import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { formatCurrency } from "../utils/formatters";
import { localTimeToUTCISO, parseISOInCompanyTimezone } from "../utils/calendarTimezone";
import { safeReadBody, bestErrorMessage, isOutsideHoursWarning } from "../utils/apiHelpers";
import {
  ArrowLeft,
  User,
  CalendarBlank,
  Clock,
  Plus,
  NotePencil,
  Receipt,
  Wrench,
  CheckCircle,
  Play,
  X,
  PencilSimple,
  Trash,
  FileText,
  MapPin,
  Phone,
  Image as ImageIcon,
  Camera,
  ListBullets,
  Bell,
  Lightning,
  ArrowsClockwise,
} from "phosphor-react";
import JobTimeline from "../components/JobTimeline";
import { NoteInput, NotesList } from "../components/NotesPhotos";
import { PhotoGallery, UploadPhotoModal } from "../components/photo";
import { useAssignableTechnicians } from "../hooks/useAssignableTechnicians";
import { useFirstValueOnboarding } from "../contexts/FirstValueOnboardingContext";
import { GuidedJobDetailOverlay } from "../components/FirstValueOnboardingOverlay";
import { useServiceTypes } from "../hooks/useServiceTypes";
import { useEntitlementsWithFetch } from "../contexts/EntitlementContext";
import ScheduleVisitModal from "../components/ScheduleVisitModal";
import LineItemsEditor from "../components/LineItemsEditor";
import TotalsSummary from "../components/TotalsSummary";
import VoiceNoteContractor from "../components/VoiceNoteContractor";
import CustomerJobSummary from "../components/CustomerJobSummary";
import SpanishTranslationCTA from "../components/SpanishTranslationCTA";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timelineKey, setTimelineKey] = useState(0); // For refreshing timeline
  
  // First-value onboarding context
  const { isGuided, step: fvoStep, starterJobId } = useFirstValueOnboarding();
  const isOnboardingJob = isGuided && fvoStep === 'job_detail' && starterJobId === jobId;
  
  // Modals
  const [visitModalOpen, setVisitModalOpen] = useState(false);
  const [visitDetailModalOpen, setVisitDetailModalOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [techModalOpen, setTechModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  
  // Reminder CTA state
  const [reminderStatus, setReminderStatus] = useState({ feature_active: false, entitled: false, show_cta: false });
  
  // Spanish Translation CTA state
  const [spanishCTAOpen, setSpanishCTAOpen] = useState(false);
  const [spanishTechIds, setSpanishTechIds] = useState([]); // IDs of Spanish techs in current selection
  const [pendingSaveCallback, setPendingSaveCallback] = useState(null); // Callback to execute on dismiss
  const [isSavingAssignment, setIsSavingAssignment] = useState(false); // Loading state for technician assignment
  
  // In-flight operation tracking for save integrity (prevents double-submit)
  const assignOpRef = useRef(0); // Increments on each assign attempt
  const assignAbortRef = useRef(null); // AbortController for canceling in-flight requests
  const visitSaveOpRef = useRef(0); // Increments on each visit save attempt
  const visitSaveAbortRef = useRef(null); // AbortController for canceling in-flight requests
  
  // Visit notes/photos data
  const [visitNotes, setVisitNotes] = useState([]);
  const [visitPhotos, setVisitPhotos] = useState([]);
  const [loadingVisitData, setLoadingVisitData] = useState(false);
  const [photoUploadModalOpen, setPhotoUploadModalOpen] = useState(false);
  
  // Visit edit state
  const [isEditingVisit, setIsEditingVisit] = useState(false);
  const [isSavingVisit, setIsSavingVisit] = useState(false); // Loading state for visit save
  const [editVisitForm, setEditVisitForm] = useState({
    date: new Date(),
    startTime: "09:00",
    endTime: "10:00",
    assigned_technicians: [],
    customer_address_id: null,
  });
  const [visitAddresses, setVisitAddresses] = useState([]);
  const [showDeleteVisitConfirm, setShowDeleteVisitConfirm] = useState(false);
  const [showOutsideHoursWarning, setShowOutsideHoursWarning] = useState(false);
  const [editVisitDatePickerOpen, setEditVisitDatePickerOpen] = useState(false);
  
  // Use the canonical hook for assignable technicians
  const { 
    technicians, 
    allTechnicians, 
    canAssignTechnician,
    refetch: refetchTechnicians 
  } = useAssignableTechnicians();
  
  // Use the service types hook
  const { 
    serviceTypes, 
    loading: serviceTypesLoading,
    updateUserPreference,
    getIdBySlug,
    getNameBySlug,
  } = useServiceTypes();
  
  // Use entitlements for Spanish Translation check
  const { hasSpanishTranslation } = useEntitlementsWithFetch();
  
  const [selectedTechs, setSelectedTechs] = useState([]);
  
  // Business hours (fetched for visit modal defaults)
  const [businessHours, setBusinessHours] = useState({ start: "09:00", end: "17:00", timezone: null });
  
  // Edit job form
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    service_type: "",
    priority: "normal",
    status: "draft",
  });
  
  // Invoice items - quantity empty by default
  const [invoiceItems, setInvoiceItems] = useState([
    { description: "", quantity: "", price: "", is_taxable: false }
  ]);
  const [invoiceTitle, setInvoiceTitle] = useState("");
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [taxRatePercent, setTaxRatePercent] = useState(0);
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  useEffect(() => {
    fetchJob();
    fetchReminderStatus();
    fetchBusinessHours();
    fetchTaxRate();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const response = await fetch(`${API}/jobs/${jobId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setJob(data);
        setSelectedTechs(data.assigned_technicians || []);
        setEditForm({
          title: data.title || "",
          description: data.description || "",
          service_type: data.service_type || "general",
          priority: data.priority || "normal",
          status: data.status || "draft",
        });
        // Initialize invoice title from job title
        setInvoiceTitle(data.title || "");
      }
    } catch (error) {
      console.error("Error fetching job:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxRate = async () => {
    try {
      const response = await fetch(`${API}/tenants/me`, { credentials: "include" });
      if (response.ok) {
        const tenant = await response.json();
        setTaxRatePercent(tenant.tax_rate || 0);
      }
    } catch (error) {
      console.error("Error fetching tax rate:", error);
    }
  };

  const fetchReminderStatus = async () => {
    try {
      const response = await fetch(`${API}/tenants/reminder-status`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setReminderStatus(data);
      }
    } catch (error) {
      console.error("Error fetching reminder status:", error);
    }
  };

  const fetchBusinessHours = async () => {
    try {
      const response = await fetch(`${API}/calendar/business-hours`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setBusinessHours({
          start: data.business_hours_start || "09:00",
          end: data.business_hours_end || "17:00",
          timezone: data.timezone || "America/New_York",
        });
      }
    } catch (error) {
      console.error("Error fetching business hours:", error);
    }
  };

  // Callback when visit is scheduled from the modal
  const handleVisitScheduled = () => {
    setTimelineKey(prev => prev + 1);
    fetchJob();
  };

  const updateJobStatus = async (newStatus) => {
    try {
      const response = await fetch(`${API}/jobs/${jobId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          ...job, 
          status: newStatus,
          customer_id: job.customer_id,
          title: job.title,
        }),
      });
      if (response.ok) {
        toast.success(`Job marked as ${newStatus.replace("_", " ")}`);
        fetchJob();
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const saveJobEdit = async () => {
    try {
      const response = await fetch(`${API}/jobs/${jobId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          customer_id: job.customer_id,
          title: editForm.title,
          description: editForm.description,
          service_type: editForm.service_type,
          priority: editForm.priority,
          status: editForm.status,
          assigned_technicians: selectedTechs,
        }),
      });
      if (response.ok) {
        toast.success("Job updated");
        setEditModalOpen(false);
        fetchJob();
      } else {
        toast.error("Failed to update job");
      }
    } catch (error) {
      toast.error("Failed to update job");
    }
  };

  // Helper to find all Spanish-speaking techs in selection
  const findSpanishTechIds = (techIds) => {
    if (hasSpanishTranslation()) return []; // Entitled - no Spanish techs need CTA
    
    return techIds.filter(techId => {
      const tech = allTechnicians.find(t => t.technician_id === techId);
      return tech && tech.preferred_language === "es";
    });
  };

  // CTA callbacks (for inline panel inside Assign Technicians modal)
  const handleCTAContinue = () => {
    // User clicked "Continue without upgrade" - proceed with save immediately
    setSpanishCTAOpen(false);
    if (pendingSaveCallback) {
      const callback = pendingSaveCallback;
      setPendingSaveCallback(null);
      
      // GUARD: Verify callback is a direct callable function
      if (typeof callback !== 'function') {
        console.error('[handleCTAContinue] CRITICAL: pendingSaveCallback is not a function', { 
          type: typeof callback, 
          value: callback 
        });
        setIsSavingAssignment(false);
        setIsSavingVisit(false);
        toast.error('Save failed - please try again');
        return;
      }
      
      // Execute the pending save
      callback();
    }
  };

  const handleCTAUpgrade = () => {
    // User clicked "Upgrade" - close CTA, don't save (component navigates)
    setSpanishCTAOpen(false);
    setPendingSaveCallback(null);
    setSpanishTechIds([]);
  };

  const handleCTARemoveSpanishTechs = () => {
    // User clicked "Use English instead" - remove Spanish techs from selection, no save
    setSpanishCTAOpen(false);
    setPendingSaveCallback(null);
    
    // Remove Spanish techs from selection
    setSelectedTechs(prev => prev.filter(id => !spanishTechIds.includes(id)));
    setSpanishTechIds([]);
    // Don't save - user needs to pick new techs
  };

  // Actual technician assignment
  const doAssignTechnicians = async (techsToAssign) => {
    // Prevent double-clicks while already saving
    if (isSavingAssignment) {
      console.log('[doAssignTechnicians] Already saving, ignoring duplicate call');
      return;
    }
    
    // Abort any prior in-flight request and start new operation
    assignAbortRef.current?.abort?.();
    assignOpRef.current += 1;
    const opId = assignOpRef.current;
    const abortController = new AbortController();
    assignAbortRef.current = abortController;
    
    setIsSavingAssignment(true);
    console.log('[doAssignTechnicians] Starting save', { opId, isSavingAssignment: true });
    
    // 12-SECOND WATCHDOG: Shows toast but does NOT unlock UI (prevents double-submit)
    const watchdogTimeout = setTimeout(() => {
      // Only show watchdog if this operation is still current
      if (opId === assignOpRef.current) {
        console.warn('[doAssignTechnicians] WATCHDOG: Save taking >12s', {
          opId,
          jobId,
          techsToAssign,
        });
        toast('Still working…', {
          description: 'You can keep waiting, or cancel and retry.',
          action: {
            label: 'Cancel & Retry',
            onClick: () => {
              // Abort current request and retry
              if (opId === assignOpRef.current) {
                console.log('[doAssignTechnicians] User requested Cancel & Retry', { opId });
                abortController.abort();
                // Unlock and retry after short delay for abort to propagate
                setIsSavingAssignment(false);
                setTimeout(() => doAssignTechnicians(techsToAssign), 50);
              }
            },
          },
          duration: 10000,
        });
      }
    }, 12000);
    
    try {
      const response = await fetch(`${API}/jobs/${jobId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          customer_id: job.customer_id,
          title: job.title,
          description: job.description,
          service_type: job.service_type,
          priority: job.priority,
          status: job.status,
          assigned_technicians: techsToAssign,
        }),
        signal: abortController.signal,
      });
      if (response.ok) {
        toast.success("Technicians assigned");
        setSpanishCTAOpen(false); // Ensure CTA is closed
        setSpanishTechIds([]);
        setTechModalOpen(false);
        fetchJob();
      } else {
        toast.error("Failed to assign technicians");
      }
    } catch (error) {
      // Handle user-initiated abort (Cancel & Retry) gracefully
      if (error.name === 'AbortError') {
        console.log('[doAssignTechnicians] Request aborted (user canceled)', { opId });
        // Don't show error toast - user initiated this via Cancel & Retry
        return; // Exit early - retry will start a new operation
      }
      toast.error("Failed to assign technicians");
    } finally {
      // Clear watchdog timeout
      clearTimeout(watchdogTimeout);
      // Only unlock if this is still the current operation (prevents stale finally from unlocking)
      if (opId === assignOpRef.current) {
        setIsSavingAssignment(false);
        console.log('[doAssignTechnicians] Complete', { opId, isSavingAssignment: false });
      } else {
        console.log('[doAssignTechnicians] Stale operation complete, not unlocking', { opId, currentOpId: assignOpRef.current });
      }
    }
  };

  // Save handler - shows CTA if Spanish techs selected, otherwise saves immediately
  const assignTechnicians = () => {
    const spanishIds = findSpanishTechIds(selectedTechs);
    
    if (spanishIds.length > 0) {
      // Spanish techs selected without entitlement - show CTA
      setSpanishTechIds(spanishIds);
      // Store callback to execute on dismiss (single function, not nested)
      setPendingSaveCallback(() => doAssignTechnicians.bind(null, selectedTechs));
      setSpanishCTAOpen(true);
      return; // Don't save yet - wait for CTA decision
    }
    
    // No Spanish techs - proceed with save
    doAssignTechnicians(selectedTechs);
  };

  const startVisit = async (visitId) => {
    try {
      const response = await fetch(`${API}/visits/${visitId}/start`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Visit started");
        fetchJob();
      }
    } catch (error) {
      toast.error("Failed to start visit");
    }
  };

  const completeVisit = async (visitId) => {
    try {
      const response = await fetch(`${API}/visits/${visitId}/complete`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Visit completed");
        fetchJob();
      }
    } catch (error) {
      toast.error("Failed to complete visit");
    }
  };

  const openVisitDetail = async (visit) => {
    // Refresh job data to get latest visit status before showing detail
    await fetchJob();
    
    // Get the potentially updated visit data
    const response = await fetch(`${API}/jobs/${jobId}`, { credentials: "include" });
    if (response.ok) {
      const data = await response.json();
      // Find the updated visit in the fresh data
      const updatedVisit = data.visits?.find(v => v.visit_id === visit.visit_id) || visit;
      setSelectedVisit(updatedVisit);
    } else {
      setSelectedVisit(visit);
    }
    
    setVisitDetailModalOpen(true);
    setIsEditingVisit(false);
    setLoadingVisitData(true);
    
    // Initialize edit form with visit data - parse in company timezone
    const companyTimezone = businessHours?.timezone;
    const visitDate = parseISOInCompanyTimezone(visit.scheduled_start, companyTimezone) || new Date(visit.scheduled_start);
    const startTime = format(visitDate, "HH:mm");
    const endDate = visit.scheduled_end 
      ? parseISOInCompanyTimezone(visit.scheduled_end, companyTimezone) || new Date(visit.scheduled_end)
      : null;
    const endTime = endDate ? format(endDate, "HH:mm") : "10:00";
    
    setEditVisitForm({
      date: visitDate,
      startTime,
      endTime,
      assigned_technicians: visit.assigned_technicians || [],
      customer_address_id: visit.customer_address_id || null,
    });
    
    // Fetch customer addresses for this job
    if (job?.customer_id) {
      try {
        const addrRes = await fetch(`${API}/customers/${job.customer_id}/addresses`, { credentials: "include" });
        if (addrRes.ok) {
          const data = await addrRes.json();
          setVisitAddresses(data.addresses || []);
        }
      } catch (error) {
        console.error("Error fetching addresses:", error);
      }
    }
    
    try {
      const [notesRes, photosRes] = await Promise.all([
        fetch(`${API}/notes/visit/${visit.visit_id}`, { credentials: "include" }),
        fetch(`${API}/photos/visit/${visit.visit_id}`, { credentials: "include" }),
      ]);
      
      if (notesRes.ok) {
        const data = await notesRes.json();
        setVisitNotes(data.notes || []);
      }
      if (photosRes.ok) {
        const data = await photosRes.json();
        setVisitPhotos(data.photos || []);
      }
    } catch (error) {
      console.error("Error fetching visit data:", error);
    } finally {
      setLoadingVisitData(false);
    }
  };

  const refreshVisitData = async () => {
    if (!selectedVisit) return;
    
    try {
      const [notesRes, photosRes] = await Promise.all([
        fetch(`${API}/notes/visit/${selectedVisit.visit_id}`, { credentials: "include" }),
        fetch(`${API}/photos/visit/${selectedVisit.visit_id}`, { credentials: "include" }),
      ]);
      
      if (notesRes.ok) {
        const data = await notesRes.json();
        setVisitNotes(data.notes || []);
      }
      if (photosRes.ok) {
        const data = await photosRes.json();
        setVisitPhotos(data.photos || []);
      }
      // Also refresh timeline
      setTimelineKey(prev => prev + 1);
    } catch (error) {
      console.error("Error refreshing visit data:", error);
    }
  };

  // Check if time is outside business hours
  const isOutsideBusinessHours = (time) => {
    const [h] = time.split(":").map(Number);
    const [startH] = businessHours.start.split(":").map(Number);
    const [endH] = businessHours.end.split(":").map(Number);
    return h < startH || h >= endH;
  };

  // Save visit edits
  // Actual visit edit save (after CTA check)
  const doSaveVisitEdit = async (confirmed = false) => {
    if (!selectedVisit) return;
    
    // Prevent double-clicks while already saving
    if (isSavingVisit) {
      console.log('[doSaveVisitEdit] Already saving, ignoring duplicate call');
      return;
    }
    
    // Abort any prior in-flight request and start new operation
    visitSaveAbortRef.current?.abort?.();
    visitSaveOpRef.current += 1;
    const opId = visitSaveOpRef.current;
    const abortController = new AbortController();
    visitSaveAbortRef.current = abortController;
    
    setIsSavingVisit(true);
    console.log('[doSaveVisitEdit] Starting save', { opId, isSavingVisit: true });
    
    // 12-SECOND WATCHDOG: Shows toast but does NOT unlock UI (prevents double-submit)
    const watchdogTimeout = setTimeout(() => {
      // Only show watchdog if this operation is still current
      if (opId === visitSaveOpRef.current) {
        console.warn('[doSaveVisitEdit] WATCHDOG: Save taking >12s', {
          opId,
          visitId: selectedVisit?.visit_id,
          assignedTechs: editVisitForm.assigned_technicians,
        });
        toast('Still working…', {
          description: 'You can keep waiting, or cancel and retry.',
          action: {
            label: 'Cancel & Retry',
            onClick: () => {
              // Abort current request and retry
              if (opId === visitSaveOpRef.current) {
                console.log('[doSaveVisitEdit] User requested Cancel & Retry', { opId });
                abortController.abort();
                // Unlock and retry after short delay for abort to propagate
                setIsSavingVisit(false);
                setTimeout(() => doSaveVisitEdit(confirmed), 50);
              }
            },
          },
          duration: 10000,
        });
      }
    }, 12000);
    
    // Instrumentation: Log entry point
    console.log('[doSaveVisitEdit] START', { 
      opId,
      visitId: selectedVisit.visit_id, 
      confirmed, 
      startTime: editVisitForm.startTime,
      endTime: editVisitForm.endTime 
    });
    
    // Check for outside business hours (frontend check)
    if (!confirmed && (isOutsideBusinessHours(editVisitForm.startTime) || isOutsideBusinessHours(editVisitForm.endTime))) {
      console.log('[doSaveVisitEdit] Frontend outside-hours check triggered, showing warning');
      setShowOutsideHoursWarning(true);
      clearTimeout(watchdogTimeout);
      setIsSavingVisit(false); // Unlock for warning interaction
      return;
    }
    
    try {
      // Convert user-selected times (in company timezone) to UTC
      const companyTimezone = businessHours?.timezone;
      const utcStart = localTimeToUTCISO(editVisitForm.date, editVisitForm.startTime, companyTimezone);
      const utcEnd = localTimeToUTCISO(editVisitForm.date, editVisitForm.endTime, companyTimezone);
      
      const payload = {
        scheduled_start: utcStart,
        scheduled_end: utcEnd,
        assigned_technicians: editVisitForm.assigned_technicians,
        customer_address_id: editVisitForm.customer_address_id,
        override_business_hours: confirmed,
      };
      
      // Instrumentation: Log request details
      console.log('[doSaveVisitEdit] Request:', {
        opId,
        url: `${API}/visits/${selectedVisit.visit_id}/reschedule`,
        payload,
        timezone: companyTimezone
      });
      
      const response = await fetch(`${API}/visits/${selectedVisit.visit_id}/reschedule`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
        signal: abortController.signal,
      });
      
      // Read response body exactly once using safe helper
      const parsed = await safeReadBody(response);
      
      // Instrumentation: Log response
      console.log('[doSaveVisitEdit] Response:', {
        opId,
        status: response.status,
        ok: response.ok,
        data: parsed.data,
        rawLength: parsed.raw?.length
      });
      
      if (response.ok) {
        toast.success("Visit updated");
        setIsEditingVisit(false);
        setShowOutsideHoursWarning(false);
        setSpanishCTAOpen(false); // Ensure CTA is closed
        setSpanishTechIds([]);
        fetchJob();
        setVisitDetailModalOpen(false);
      } else if (response.status === 422 && isOutsideHoursWarning(parsed)) {
        // Backend says outside business hours - show warning
        console.log('[doSaveVisitEdit] 422 outside-hours warning from backend, showing warning dialog');
        setShowOutsideHoursWarning(true);
      } else {
        // Show the real backend error message with HTTP status
        const errorMsg = bestErrorMessage(response, parsed);
        console.error('[doSaveVisitEdit] Error response:', errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      // Handle user-initiated abort (Cancel & Retry) gracefully
      if (error.name === 'AbortError') {
        console.log('[doSaveVisitEdit] Request aborted (user canceled)', { opId });
        // Don't show error toast - user initiated this via Cancel & Retry
        return; // Exit early - retry will start a new operation
      }
      // Network error or fetch threw
      console.error('[doSaveVisitEdit] Fetch error:', error);
      toast.error(`Network error: ${error.message || "Connection failed"}`);
    } finally {
      // Clear watchdog timeout
      clearTimeout(watchdogTimeout);
      // Only unlock if this is still the current operation (prevents stale finally from unlocking)
      if (opId === visitSaveOpRef.current) {
        setIsSavingVisit(false);
        console.log('[doSaveVisitEdit] Complete', { opId, isSavingVisit: false });
      } else {
        console.log('[doSaveVisitEdit] Stale operation complete, not unlocking', { opId, currentOpId: visitSaveOpRef.current });
      }
    }
  };

  // Save Visit Edit handler - shows CTA if Spanish techs selected
  const saveVisitEdit = (confirmed = false) => {
    if (!selectedVisit) return;
    
    // P1 STABILITY LOCKDOWN: Spanish CTA must render inline when blocking - do not modify without reopening stability review
    // Skip Spanish check if this is a confirmed override (e.g., outside hours)
    if (!confirmed) {
      const spanishIds = findSpanishTechIds(editVisitForm.assigned_technicians);
      
      if (spanishIds.length > 0) {
        // Spanish techs selected without entitlement - show CTA
        setSpanishTechIds(spanishIds);
        // Store callback to execute on dismiss (single function, not nested)
        setPendingSaveCallback(() => doSaveVisitEdit);
        setSpanishCTAOpen(true);
        return; // Don't save yet - wait for CTA decision
      }
    }
    
    // No Spanish techs or confirmed - proceed with save
    doSaveVisitEdit(confirmed);
  };

  // Handle removing Spanish techs from visit edit form
  const handleVisitCTARemoveSpanishTechs = () => {
    setSpanishCTAOpen(false);
    setPendingSaveCallback(null);
    
    // Remove Spanish techs from edit form selection
    setEditVisitForm(prev => ({
      ...prev,
      assigned_technicians: prev.assigned_technicians.filter(id => !spanishTechIds.includes(id))
    }));
    setSpanishTechIds([]);
  };

  // Delete visit
  const deleteVisit = async () => {
    if (!selectedVisit) return;
    
    try {
      const response = await fetch(`${API}/visits/${selectedVisit.visit_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Visit deleted");
        setShowDeleteVisitConfirm(false);
        setVisitDetailModalOpen(false);
        fetchJob();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete visit");
      }
    } catch (error) {
      toast.error("Failed to delete visit");
    }
  };

  const createInvoice = async (e) => {
    if (e) e.preventDefault();
    setCreatingInvoice(true);
    try {
      const response = await fetch(`${API}/invoices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          customer_id: job.customer_id,
          job_id: job.job_id,
          title: invoiceTitle,
          items: invoiceItems.filter(i => i.description),
          notes: invoiceNotes,
        }),
      });
      if (response.ok) {
        const newInvoice = await response.json();
        toast.success("Invoice created");
        setInvoiceModalOpen(false);
        // Navigate to invoice detail page for further editing
        navigate(`/invoices?view=${newInvoice.invoice_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create invoice");
      }
    } catch (error) {
      toast.error("Failed to create invoice");
    } finally {
      setCreatingInvoice(false);
    }
  };

  // Invoice line item handlers (gold-standard pattern)
  const updateInvoiceLineItem = (index, field, value) => {
    const newItems = [...invoiceItems];
    if (field === "quantity") {
      // Allow empty, store as string for input control
      newItems[index][field] = value;
    } else if (field === "is_taxable") {
      newItems[index][field] = value;
    } else {
      newItems[index][field] = value;
    }
    setInvoiceItems(newItems);
  };

  const addInvoiceLineItem = () => {
    setInvoiceItems([...invoiceItems, { description: "", quantity: "", price: "", is_taxable: false }]);
  };

  const removeInvoiceLineItem = (index) => {
    if (invoiceItems.length > 1) {
      setInvoiceItems(invoiceItems.filter((_, i) => i !== index));
    }
  };

  const formatInvoicePrice = (index) => {
    const newItems = [...invoiceItems];
    const val = parseFloat(newItems[index].price);
    if (!isNaN(val) && val > 0) {
      newItems[index].price = val.toFixed(2);
    } else if (newItems[index].price === "" || newItems[index].price === "0") {
      newItems[index].price = "";
    }
    setInvoiceItems(newItems);
  };

  const handleInvoiceCatalogSelect = (index, catalogItem) => {
    const newItems = [...invoiceItems];
    const currentQuantity = newItems[index].quantity;
    newItems[index] = {
      ...newItems[index],
      description: catalogItem.description,
      price: catalogItem.price.toFixed(2),
      is_taxable: catalogItem.is_taxable,
      quantity: currentQuantity !== "" ? currentQuantity : "",
      catalog_item_id: catalogItem.catalog_item_id,
    };
    setInvoiceItems(newItems);
  };

  // Reset invoice form when opening modal
  const openInvoiceModal = () => {
    setInvoiceTitle(job?.title || "");
    setInvoiceItems([{ description: "", quantity: "", price: "", is_taxable: false }]);
    setInvoiceNotes("");
    setInvoiceModalOpen(true);
  };

  const deleteJob = async () => {
    if (!window.confirm("Are you sure you want to delete this job? This cannot be undone.")) return;
    try {
      const response = await fetch(`${API}/jobs/${jobId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Job deleted");
        navigate("/jobs");
      }
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const toggleTechSelection = (techId) => {
    setSelectedTechs(prev => 
      prev.includes(techId) 
        ? prev.filter(id => id !== techId)
        : [...prev, techId]
    );
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-zinc-200 rounded-sm" />
        <div className="h-64 bg-zinc-200 rounded-sm" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-500">Job not found</p>
        <Link to="/jobs">
          <Button variant="link" className="text-[#FF6600]">Back to Jobs</Button>
        </Link>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      draft: "bg-zinc-100 text-zinc-700 border-zinc-300",
      scheduled: "bg-blue-50 text-blue-700 border-blue-300",
      in_progress: "bg-amber-50 text-amber-700 border-amber-300",
      completed: "bg-emerald-50 text-emerald-700 border-emerald-300",
      canceled: "bg-red-50 text-red-700 border-red-300",
    };
    return colors[status] || colors.draft;
  };

  return (
    <div className="space-y-6" data-testid="job-detail">
      {/* First-value onboarding overlay */}
      {isOnboardingJob && <GuidedJobDetailOverlay />}
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link to="/jobs">
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft size={18} />
            Back to Jobs
          </Button>
        </Link>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="border-2 gap-2" onClick={() => setEditModalOpen(true)} data-testid="edit-job-btn">
            <PencilSimple size={16} />
            Edit
          </Button>
          <Button variant="outline" size="sm" className="border-2 gap-2 text-red-600 hover:bg-red-50" onClick={deleteJob} data-testid="delete-job-btn">
            <Trash size={16} />
            Delete
          </Button>
        </div>
      </div>

      {/* Job Info Card */}
      <Card className="border-2 border-zinc-200">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <h1 className="font-heading text-2xl font-bold uppercase">{job.title}</h1>
                <Badge variant="outline" className={getStatusColor(job.status)}>
                  {job.status.replace("_", " ")}
                </Badge>
                <Badge variant="secondary" className="capitalize">{job.priority}</Badge>
              </div>
              {job.description && <p className="text-zinc-600 mb-4">{job.description}</p>}
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-zinc-400 text-xs uppercase mb-1">Customer</p>
                  <Link to={`/customers/${job.customer_id}`} className="flex items-center gap-2 text-zinc-700 hover:text-[#FF6600]">
                    <User size={16} />
                    <span className="font-medium">{job.customer?.name}</span>
                  </Link>
                  {job.customer?.phone && (
                    <a href={`tel:${job.customer.phone}`} className="flex items-center gap-2 text-zinc-500 text-xs mt-1">
                      <Phone size={12} />
                      {job.customer.phone}
                    </a>
                  )}
                </div>
                <div>
                  <p className="text-zinc-400 text-xs uppercase mb-1">Service Type</p>
                  <p className="font-medium capitalize">{getNameBySlug(job.service_type) || job.service_type}</p>
                </div>
                <div>
                  <p className="text-zinc-400 text-xs uppercase mb-1">Created</p>
                  <p className="font-medium">{new Date(job.created_at).toLocaleDateString()}</p>
                </div>
                {job.completed_at && (
                  <div>
                    <p className="text-zinc-400 text-xs uppercase mb-1">Completed</p>
                    <p className="font-medium">{new Date(job.completed_at).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 min-w-[180px]">
              {job.status === "draft" && (
                <>
                  <Button onClick={() => setVisitModalOpen(true)} className="bg-[#FF6600] hover:bg-[#CC5200] text-white gap-2" data-testid="schedule-visit-btn">
                    <CalendarBlank size={18} />
                    Schedule Visit
                  </Button>
                  <Button onClick={() => updateJobStatus("scheduled")} variant="outline" className="border-2 gap-2">
                    <CheckCircle size={18} />
                    Mark Scheduled
                  </Button>
                </>
              )}
              {job.status === "scheduled" && (
                <>
                  <Button onClick={() => updateJobStatus("in_progress")} className="bg-[#FF6600] hover:bg-[#CC5200] text-white gap-2">
                    <Play size={18} />
                    Start Work
                  </Button>
                  <Button onClick={() => setVisitModalOpen(true)} variant="outline" className="border-2 gap-2">
                    <Plus size={18} />
                    Add Visit
                  </Button>
                </>
              )}
              {job.status === "in_progress" && (
                <>
                  <Button onClick={() => updateJobStatus("completed")} className="bg-[#FF6600] hover:bg-[#CC5200] text-white gap-2">
                    <CheckCircle size={18} />
                    Complete Job
                  </Button>
                  <Button onClick={() => setVisitModalOpen(true)} variant="outline" className="border-2 gap-2">
                    <Plus size={18} />
                    Add Visit
                  </Button>
                </>
              )}
              {job.status === "completed" && !job.invoiced && (
                <Button onClick={openInvoiceModal} className="bg-[#FF6600] hover:bg-[#CC5200] text-white gap-2" data-testid="create-invoice-btn">
                  <Receipt size={18} />
                  Create Invoice
                </Button>
              )}
              {job.invoiced && (
                <Badge className="bg-emerald-100 text-emerald-700 justify-center py-2">
                  <CheckCircle size={16} className="mr-2" />
                  Invoiced
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Assigned Technicians */}
        <Card className="border-2 border-zinc-200">
          <CardHeader className="border-b border-zinc-100 py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <Wrench size={20} />
                Assigned Technicians
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setTechModalOpen(true)} className="text-[#FF6600]" data-testid="manage-techs-btn">
                <PencilSimple size={16} className="mr-1" />
                Manage
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {job.technicians?.length > 0 ? (
              <div className="space-y-3">
                {job.technicians.map((tech) => (
                  <div key={tech.technician_id} className="flex items-center gap-3 p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                    <div className="w-10 h-10 bg-[#FF6600]/10 border border-[#FF6600]/30 flex items-center justify-center rounded-sm">
                      <Wrench size={20} className="text-[#FF6600]" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{tech.name}</p>
                      <p className="text-xs text-zinc-500">{tech.email}</p>
                    </div>
                    {tech.preferred_language === "es" && (
                      <Badge variant="secondary" className="text-xs">ES</Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Wrench size={32} className="mx-auto mb-2 text-zinc-300" />
                <p className="text-zinc-400 text-sm mb-3">No technicians assigned</p>
                <Button variant="outline" size="sm" className="border-2" onClick={() => setTechModalOpen(true)}>
                  <Plus size={16} className="mr-2" />
                  Assign Technicians
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scheduled Visits */}
        <Card className="border-2 border-zinc-200">
          <CardHeader className="border-b border-zinc-100 py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <CalendarBlank size={20} />
                Scheduled Visits
              </CardTitle>
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={fetchJob} 
                  className="text-zinc-500 hover:text-zinc-700"
                  title="Refresh visits"
                  data-testid="refresh-visits-btn"
                >
                  <ArrowsClockwise size={16} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setVisitModalOpen(true)} className="text-[#FF6600]" data-testid="add-visit-btn">
                  <Plus size={16} className="mr-1" />
                  Add
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {job.visits?.length > 0 ? (
              <div className="space-y-3">
                {/* Sort visits chronologically by scheduled_start (earliest first) */}
                {[...job.visits]
                  .sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start))
                  .map((visit) => (
                  <div 
                    key={visit.visit_id} 
                    className="p-4 bg-zinc-50 border border-zinc-200 rounded-sm cursor-pointer hover:bg-zinc-100 transition-colors"
                    onClick={() => openVisitDetail(visit)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium">{format(new Date(visit.scheduled_start), "EEEE, MMM d, yyyy")}</p>
                        <p className="text-sm text-zinc-500">
                          {format(new Date(visit.scheduled_start), "h:mm a")}
                          {visit.scheduled_end && ` - ${format(new Date(visit.scheduled_end), "h:mm a")}`}
                        </p>
                      </div>
                      <Badge variant="outline" className={getStatusColor(visit.status)}>
                        {visit.status}
                      </Badge>
                    </div>
                    {visit.assigned_technicians?.length > 0 && (
                      <p className="text-xs text-zinc-400 mb-3">
                        {visit.assigned_technicians.length} technician(s) assigned
                      </p>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      {visit.status === "scheduled" && (
                        <Button 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); startVisit(visit.visit_id); }} 
                          className="bg-[#FF6600] hover:bg-[#CC5200] text-white text-xs"
                        >
                          <Play size={14} className="mr-1" />
                          Start
                        </Button>
                      )}
                      {visit.status === "in_progress" && (
                        <Button 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); completeVisit(visit.visit_id); }} 
                          className="bg-[#FF6600] hover:bg-[#CC5200] text-white text-xs"
                        >
                          <CheckCircle size={14} className="mr-1" />
                          Complete
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-xs border"
                        onClick={(e) => { e.stopPropagation(); openVisitDetail(visit); }}
                      >
                        <Camera size={14} className="mr-1" />
                        Notes & Photos
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <CalendarBlank size={32} className="mx-auto mb-2 text-zinc-300" />
                <p className="text-zinc-400 text-sm mb-3">No visits scheduled</p>
                <Button variant="outline" size="sm" className="border-2" onClick={() => setVisitModalOpen(true)}>
                  <Plus size={16} className="mr-2" />
                  Schedule Visit
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Related Documents */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Estimates */}
        <Card className="border-2 border-zinc-200">
          <CardHeader className="border-b border-zinc-100 py-4">
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <FileText size={20} />
              Estimates ({job.estimates?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {job.estimates?.length > 0 ? (
              <div className="space-y-2">
                {job.estimates.map((est) => (
                  <div key={est.estimate_id} className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                    <div>
                      <p className="font-mono text-xs text-zinc-400">{est.estimate_number}</p>
                      <p className="font-medium">{formatCurrency(est.total)}</p>
                    </div>
                    <Badge variant="outline" className={getStatusColor(est.status)}>{est.status}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-400 text-center py-4 text-sm">No estimates</p>
            )}
          </CardContent>
        </Card>

        {/* Invoices */}
        <Card className="border-2 border-zinc-200">
          <CardHeader className="border-b border-zinc-100 py-4">
            <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <Receipt size={20} />
              Invoices ({job.invoices?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {job.invoices?.length > 0 ? (
              <div className="space-y-2">
                {job.invoices.map((inv) => (
                  <div key={inv.invoice_id} className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                    <div>
                      <p className="font-mono text-xs text-zinc-400">{inv.invoice_number}</p>
                      <p className="font-medium">{formatCurrency(inv.total)}</p>
                    </div>
                    <Badge variant="outline" className={getStatusColor(inv.status)}>{inv.status}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-400 text-center py-4 text-sm">No invoices</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Voice Note from Technician */}
      <VoiceNoteContractor 
        jobId={jobId} 
        onCopyToInvoice={(summary) => {
          // Pre-fill invoice notes when modal opens
          setInvoiceNotes(summary);
          openInvoiceModal();
        }}
      />

      {/* Customer Job Summary */}
      <CustomerJobSummary
        jobId={jobId}
        onCopyToInvoiceNotes={(summary) => {
          setInvoiceNotes(summary);
          toast.success("Summary copied to invoice notes");
        }}
        onCopyToEmail={(summary) => {
          // Copy to clipboard for manual paste into email
          navigator.clipboard.writeText(summary);
          toast.success("Summary copied - paste into email when sending invoice");
        }}
      />

      {/* Activity Timeline */}
      <Card className="border-2 border-zinc-200">
        <CardHeader className="border-b border-zinc-100 py-4">
          <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
            <ListBullets size={20} />
            Activity Timeline
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <JobTimeline jobId={jobId} key={timelineKey} />
        </CardContent>
      </Card>

      {/* Schedule Visit Modal - Shared component with Calendar */}
      <ScheduleVisitModal
        open={visitModalOpen}
        onOpenChange={setVisitModalOpen}
        jobs={[]}
        technicians={technicians}
        allTechnicians={allTechnicians}
        preselectedJobId={jobId}
        preselectedJob={job}
        onVisitScheduled={handleVisitScheduled}
        businessHours={businessHours}
        onBeforeSubmit={(selectedTechIds, proceedCallback) => {
          // Check for Spanish-speaking techs without entitlement
          const spanishIds = findSpanishTechIds(selectedTechIds);
          
          if (spanishIds.length > 0) {
            // Spanish techs selected - show CTA
            setSpanishTechIds(spanishIds);
            // Store the proceed callback to execute on dismiss
            setPendingSaveCallback(() => proceedCallback);
            setSpanishCTAOpen(true);
            return false; // Don't proceed yet
          }
          return true; // No Spanish techs - proceed immediately
        }}
      />

      {/* Assign Technicians Modal */}
      <Dialog open={techModalOpen} onOpenChange={(open) => {
        if (!open) {
          // Reset CTA state when modal closes
          setSpanishCTAOpen(false);
          setSpanishTechIds([]);
          setPendingSaveCallback(null);
        }
        setTechModalOpen(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Assign Technicians</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Spanish Translation CTA - inline panel (appears when Save is clicked with Spanish techs) */}
            {spanishCTAOpen && (
              <SpanishTranslationCTA
                onContinue={handleCTAContinue}
                onUpgrade={handleCTAUpgrade}
                onRemoveSpanishTechs={handleCTARemoveSpanishTechs}
              />
            )}
            
            {/* Technician list - only show when CTA is not visible */}
            {!spanishCTAOpen && (
            <div className="max-h-60 overflow-y-auto border border-zinc-200 rounded-sm">
              {(() => {
                // Show assignable technicians + any already-selected inactive ones
                const selectedInactive = allTechnicians.filter(
                  t => t.is_active === false && selectedTechs.includes(t.technician_id)
                );
                const displayTechs = [...technicians, ...selectedInactive];
                
                if (displayTechs.length === 0) {
                  return (
                    <div className="text-center py-4 px-3">
                      <p className="text-zinc-400 mb-2">No technicians available</p>
                      <Link to="/technicians">
                        <Button size="sm" className="bg-[#FF6600] hover:bg-[#CC5200] text-white">Add Technicians</Button>
                      </Link>
                    </div>
                  );
                }
                
                return displayTechs.map((tech, idx) => {
                  const isInactive = tech.is_active === false;
                  const isSelected = selectedTechs.includes(tech.technician_id);
                  
                  return (
                    <div 
                      key={tech.technician_id} 
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-zinc-100 last:border-b-0 ${
                        isInactive 
                          ? 'bg-zinc-100 opacity-75' 
                          : isSelected
                            ? 'bg-orange-50'
                            : 'hover:bg-zinc-50'
                      }`}
                      onClick={() => {
                        // Only allow unchecking inactive technicians, not re-adding
                        if (isInactive && !isSelected) return;
                        toggleTechSelection(tech.technician_id);
                      }}
                    >
                      <Checkbox 
                        checked={isSelected} 
                        onCheckedChange={() => {
                          if (isInactive && !isSelected) return;
                          toggleTechSelection(tech.technician_id);
                        }}
                        disabled={isInactive && !isSelected}
                        className="h-4 w-4 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {tech.name}
                          {isInactive && (
                            <span className="text-xs text-red-500 ml-1">(inactive)</span>
                          )}
                        </p>
                        <p className="text-xs text-zinc-500 truncate">{tech.email}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {tech.is_owner && <Badge variant="secondary" className="text-xs px-1.5 py-0">Owner</Badge>}
                        {tech.preferred_language === "es" && <Badge variant="secondary" className="text-xs px-1.5 py-0">ES</Badge>}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            )}
            
            {/* Footer buttons - only show when CTA is not visible */}
            {!spanishCTAOpen && (
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setTechModalOpen(false)} className="flex-1 border-2">Cancel</Button>
              <Button 
                onClick={assignTechnicians} 
                disabled={isSavingAssignment}
                className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white disabled:opacity-50" 
                data-testid="confirm-assign-techs"
              >
                {isSavingAssignment ? "Saving..." : `Save (${selectedTechs.length} selected)`}
              </Button>
            </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Job Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-job-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Edit Job</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="mt-1 h-12 border-2" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="mt-1 border-2" rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Service Type</Label>
                  <Select 
                    value={editForm.service_type} 
                    onValueChange={(v) => setEditForm({ ...editForm, service_type: v })}
                    disabled={serviceTypesLoading}
                  >
                    <SelectTrigger className="mt-1 h-12 border-2">
                      <SelectValue placeholder={serviceTypesLoading ? "Loading..." : "Select service type"} />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((type) => (
                        <SelectItem key={type.service_type_id} value={type.slug}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                    <SelectTrigger className="mt-1 h-12 border-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger className="mt-1 h-12 border-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)} className="flex-1 sm:flex-none border-2">Cancel</Button>
            <Button onClick={saveJobEdit} className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Invoice Modal - Gold Standard */}
      <Dialog open={invoiceModalOpen} onOpenChange={setInvoiceModalOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="invoice-create-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              Create Invoice
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form id="job-invoice-create-form" onSubmit={createInvoice} className="space-y-4">
              {/* Customer - READ ONLY (preselected from job) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between h-5 mb-1">
                    <span className="text-sm font-medium">Customer</span>
                    <Badge variant="outline" className="text-xs text-zinc-500 border-zinc-300">
                      From Job
                    </Badge>
                  </div>
                  <div className="h-12 border-2 border-zinc-200 rounded-md px-3 flex items-center bg-zinc-50">
                    <User size={16} className="text-zinc-400 mr-2" />
                    <span className="font-medium text-zinc-700">{job?.customer?.name}</span>
                  </div>
                </div>
                <div>
                  <div className="h-5 mb-1">
                    <Label className="text-sm font-medium">Title *</Label>
                  </div>
                  <Input
                    value={invoiceTitle}
                    onChange={(e) => setInvoiceTitle(e.target.value)}
                    placeholder="Invoice title"
                    className="h-12 border-2"
                    required
                    data-testid="invoice-title-input"
                  />
                </div>
              </div>

              <LineItemsEditor
                items={invoiceItems}
                onUpdateItem={updateInvoiceLineItem}
                onAddItem={addInvoiceLineItem}
                onRemoveItem={removeInvoiceLineItem}
                onCatalogSelect={handleInvoiceCatalogSelect}
                onFormatPrice={formatInvoicePrice}
                taxRatePercent={taxRatePercent}
                testIdPrefix="job-invoice-line"
              />

              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={invoiceNotes}
                  onChange={(e) => setInvoiceNotes(e.target.value)}
                  placeholder="Payment terms or additional notes..."
                  className="mt-1 border-2"
                  rows={3}
                />
              </div>

              <TotalsSummary items={invoiceItems} taxRatePercent={taxRatePercent} />
            </form>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setInvoiceModalOpen(false)}
              className="flex-1 sm:flex-none border-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="job-invoice-create-form"
              className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white"
              disabled={!invoiceTitle || creatingInvoice}
              data-testid="confirm-create-invoice"
            >
              {creatingInvoice ? "Creating..." : "Create Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visit Detail Modal with Notes & Photos */}
      <Dialog open={visitDetailModalOpen} onOpenChange={(open) => {
        if (!open) {
          setIsEditingVisit(false);
          setShowDeleteVisitConfirm(false);
          setShowOutsideHoursWarning(false);
          // Refresh job data when modal closes to get latest visit statuses
          fetchJob();
        }
        setVisitDetailModalOpen(open);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
                <CalendarBlank size={20} />
                Visit Details
              </DialogTitle>
              {selectedVisit && !isEditingVisit && !showDeleteVisitConfirm && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditingVisit(true)}
                    className="h-9 w-9 p-0 hover:bg-zinc-100"
                    data-testid="edit-visit-btn"
                    title="Edit visit"
                  >
                    <PencilSimple size={18} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDeleteVisitConfirm(true)}
                    className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-visit-btn"
                    title="Delete visit"
                  >
                    <Trash size={18} />
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>

          {selectedVisit && (
            <div className="space-y-4">
              {/* Delete Confirmation */}
              {showDeleteVisitConfirm && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-sm">
                  <p className="text-sm font-medium text-red-800 mb-3">Delete this visit?</p>
                  <p className="text-xs text-red-600 mb-3">This action cannot be undone. Notes and photos will also be deleted.</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeleteVisitConfirm(false)}
                      className="border-red-200"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={deleteVisit}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="confirm-delete-visit"
                    >
                      Delete Visit
                    </Button>
                  </div>
                </div>
              )}

              {/* Outside Hours Warning */}
              {showOutsideHoursWarning && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
                  <p className="text-sm font-medium text-amber-800 mb-3">
                    This visit is scheduled outside business hours ({businessHours.start} - {businessHours.end})
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowOutsideHoursWarning(false)}
                      className="border-amber-200"
                    >
                      Go Back
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => saveVisitEdit(true)}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      Schedule Anyway
                    </Button>
                  </div>
                </div>
              )}

              {/* Visit Info - View Mode */}
              {!isEditingVisit && !showDeleteVisitConfirm && !showOutsideHoursWarning && (
                <div className="p-4 bg-zinc-50 rounded-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{format(new Date(selectedVisit.scheduled_start), "EEEE, MMMM d, yyyy")}</p>
                      <p className="text-sm text-zinc-500">
                        {format(new Date(selectedVisit.scheduled_start), "h:mm a")}
                        {selectedVisit.scheduled_end && ` - ${format(new Date(selectedVisit.scheduled_end), "h:mm a")}`}
                      </p>
                      {selectedVisit.assigned_technicians?.length > 0 && (
                        <p className="text-xs text-zinc-400 mt-1">
                          Assigned: {technicians.filter(t => selectedVisit.assigned_technicians.includes(t.technician_id)).map(t => t.name).join(", ")}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className={getStatusColor(selectedVisit.status)}>
                      {selectedVisit.status}
                    </Badge>
                  </div>
                </div>
              )}

              {/* Visit Info - Edit Mode */}
              {isEditingVisit && !showDeleteVisitConfirm && !showOutsideHoursWarning && (
                <div className="space-y-4 p-4 bg-zinc-50 rounded-sm">
                  {/* Spanish Translation CTA - Inline in Visit Edit */}
                  {spanishCTAOpen && (
                    <SpanishTranslationCTA
                      context="visit"
                      onContinue={handleCTAContinue}
                      onUpgrade={handleCTAUpgrade}
                      onRemoveSpanishTechs={handleVisitCTARemoveSpanishTechs}
                    />
                  )}

                  {/* Only show edit form when CTA is not open */}
                  {!spanishCTAOpen && (
                    <>
                      {/* Job & Customer - Not Editable */}
                      <div className="text-sm text-zinc-500">
                        <span className="font-medium">Job:</span> {job?.title} • <span className="font-medium">Customer:</span> {job?.customer?.name}
                      </div>

                  {/* Date */}
                  <div>
                    <Label>Date</Label>
                    <Popover open={editVisitDatePickerOpen} onOpenChange={setEditVisitDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full mt-1 h-10 border-2 justify-start text-left font-normal">
                          <CalendarBlank size={16} className="mr-2" />
                          {format(editVisitForm.date, "PPP")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar 
                          mode="single" 
                          selected={editVisitForm.date} 
                          onSelect={(date) => {
                            if (date) {
                              setEditVisitForm(prev => ({ ...prev, date }));
                              setEditVisitDatePickerOpen(false);
                            }
                          }} 
                          initialFocus 
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Time */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Start Time</Label>
                      <Input
                        type="time"
                        value={editVisitForm.startTime}
                        onChange={(e) => setEditVisitForm(prev => ({ ...prev, startTime: e.target.value }))}
                        className="mt-1 h-10 border-2"
                      />
                    </div>
                    <div>
                      <Label>End Time</Label>
                      <Input
                        type="time"
                        value={editVisitForm.endTime}
                        onChange={(e) => setEditVisitForm(prev => ({ ...prev, endTime: e.target.value }))}
                        className="mt-1 h-10 border-2"
                      />
                    </div>
                  </div>

                  {/* Technicians */}
                  <div>
                    <Label>Assigned Technicians</Label>
                    <div className="mt-2 space-y-1 max-h-32 overflow-y-auto border border-zinc-200 rounded-sm p-2">
                      {technicians.map((tech) => (
                        <label key={tech.technician_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-white cursor-pointer">
                          <Checkbox
                            checked={editVisitForm.assigned_technicians.includes(tech.technician_id)}
                            onCheckedChange={(checked) => {
                              setEditVisitForm(prev => ({
                                ...prev,
                                assigned_technicians: checked
                                  ? [...prev.assigned_technicians, tech.technician_id]
                                  : prev.assigned_technicians.filter(id => id !== tech.technician_id)
                              }));
                            }}
                          />
                          <span className="text-sm">{tech.name}</span>
                          {tech.is_owner && <Badge variant="secondary" className="text-xs">Owner</Badge>}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Visit Address */}
                  {visitAddresses.length > 0 && (
                    <div>
                      <Label>Visit Address</Label>
                      <Select
                        value={editVisitForm.customer_address_id || ""}
                        onValueChange={(v) => setEditVisitForm(prev => ({ ...prev, customer_address_id: v }))}
                      >
                        <SelectTrigger className="mt-1 h-10 border-2">
                          <SelectValue placeholder="Select address" />
                        </SelectTrigger>
                        <SelectContent>
                          {visitAddresses.map((addr) => (
                            <SelectItem key={addr.id} value={addr.id}>
                              {addr.label}: {addr.street}, {addr.city}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Edit Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditingVisit(false)}
                      className="flex-1 border-2"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => saveVisitEdit()}
                      disabled={isSavingVisit}
                      className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white disabled:opacity-50"
                      data-testid="save-visit-edit"
                    >
                      {isSavingVisit ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                    </>
                  )}
                </div>
              )}

              {/* Notes & Photos Tabs - Only show when not editing */}
              {!isEditingVisit && !showDeleteVisitConfirm && !showOutsideHoursWarning && (
                <div className="border border-zinc-200 rounded-sm overflow-hidden">
                  <Tabs defaultValue="notes" className="w-full">
                    <div className="p-3 pb-0">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="notes" className="data-[state=active]:bg-[#FF6600] data-[state=active]:text-white">
                          <NotePencil size={16} className="mr-2" />
                          Notes ({visitNotes.length})
                        </TabsTrigger>
                        <TabsTrigger value="photos" className="data-[state=active]:bg-[#FF6600] data-[state=active]:text-white">
                          <ImageIcon size={16} className="mr-2" />
                          Photos ({visitPhotos.length})
                        </TabsTrigger>
                      </TabsList>
                    </div>

                    <TabsContent value="notes" className="p-3 pt-4">
                      <NoteInput 
                        visitId={selectedVisit.visit_id} 
                        onNoteAdded={refreshVisitData}
                        fullWidth={true}
                      />
                      <div className="mt-4">
                        {loadingVisitData ? (
                          <div className="py-8 text-center text-zinc-400">Loading...</div>
                        ) : (
                          <NotesList 
                            notes={visitNotes} 
                            onDelete={refreshVisitData}
                            onUpdate={refreshVisitData}
                            canEdit={true}
                          />
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="photos" className="p-3 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setPhotoUploadModalOpen(true)}
                        className="w-full border-2 border-dashed border-zinc-300 py-8"
                      >
                        <Camera size={18} className="mr-2" />
                        Upload Photo
                      </Button>
                      <div className="mt-4">
                        {loadingVisitData ? (
                          <div className="py-8 text-center text-zinc-400">Loading...</div>
                        ) : (
                          <PhotoGallery 
                            photos={visitPhotos} 
                            onDelete={refreshVisitData}
                            onUpdate={refreshVisitData}
                            canEdit={true}
                          />
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Upload Modal */}
      <UploadPhotoModal
        open={photoUploadModalOpen}
        onOpenChange={setPhotoUploadModalOpen}
        visitId={selectedVisit?.visit_id}
        onUploadComplete={refreshVisitData}
      />
    </div>
  );
}
